// src/Redux/thunks/sharingThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosExtraction } from "../../../api/axios";
import { EXTRACTION_PATHS } from "../../../api/endpoints";

/** 🔧 Helper erreur */
const toRejection = (err, defaultMsg = "Une erreur est survenue") =>
  err?.response?.data?.message ||
  err?.response?.data?.detail ||
  err?.message ||
  defaultMsg;

const isAlreadyRevokedOrMissingAccess = (payload) => {
  const text = `${payload?.message || ""} ${payload?.detail || ""}`.toLowerCase();
  const hasNoopMessage =
    text.includes("deja revoque") ||
    text.includes("déjà révoqué") ||
    text.includes("acces non trouve") ||
    text.includes("accès non trouvé");

  if (hasNoopMessage) return true;

  if (!Array.isArray(payload?.results) || payload.results.length === 0) return false;

  return payload.results.every((item) => {
    const errText = String(item?.error || "").toLowerCase();
    return (
      item?.success === false &&
      (errText.includes("deja revoque") ||
        errText.includes("déjà révoqué") ||
        errText.includes("acces non trouve") ||
        errText.includes("accès non trouvé"))
    );
  });
};

// ─────────────────────────────────────────────
// SHARE DOCUMENT
// POST /extraction/sharing/:documentId/share
// Body: { doctorIds: string[], expiresInHours?: number | null }
// ─────────────────────────────────────────────
export const shareDocument = createAsyncThunk(
  "sharing/shareDocument",
  async ({ documentId, doctorIds = [], expiresInHours = null }, { rejectWithValue }) => {
    try {
      if (!documentId) throw new Error("documentId est requis");
      if (!doctorIds.length) throw new Error("Au moins un doctorId est requis");

      const normalizedDoctorIds = doctorIds
        .map((id) => String(id ?? "").replace(/^DR_/i, "").trim())
        .filter(Boolean);

      const payload = {
        doctorIds: normalizedDoctorIds,
        ...(expiresInHours != null && { expiresInHours }),
      };

      const response = await axiosExtraction.post(
        EXTRACTION_PATHS.SHARE_DOCUMENT(documentId),
        payload
      );

      // ✅ Gérer les réponses 204 No Content (pas de body)
      const data = response?.data || {};

      return {
        success: true,
        documentId,
        doctorIds: normalizedDoctorIds,
        data: data?.data || data,
        message: data?.message || "Document partagé avec succès",
      };
    } catch (err) {
      return rejectWithValue(toRejection(err, "Erreur lors du partage du document"));
    }
  }
);

// ─────────────────────────────────────────────
// SHARE MULTIPLE DOCUMENTS (batch)
// ─────────────────────────────────────────────
export const shareDocuments = createAsyncThunk(
  "sharing/shareDocuments",
  async (
    { documents = [], doctorIds = [], expiresInHours = null },
    { rejectWithValue, dispatch }
  ) => {
    try {
      if (!documents.length) throw new Error("Au moins un document est requis");
      if (!doctorIds.length) throw new Error("Au moins un doctorId est requis");

      const results = [];
      const failures = [];

      for (const doc of documents) {
        const documentId = doc?.id || doc?._id;
        if (!documentId) {
          failures.push({ doc, error: "ID de document manquant" });
          continue;
        }
        try {
          const result = await dispatch(
            shareDocument({ documentId, doctorIds, expiresInHours })
          ).unwrap();
          results.push({ ...result, doc });
        } catch (err) {
          failures.push({ doc, error: err });
        }
      }

      if (results.length === 0) {
        return rejectWithValue(
          failures[0]?.error || "Échec du partage de tous les documents"
        );
      }

      return {
        success: true,
        shared: results,
        failures,
        totalShared: results.length,
        totalFailed: failures.length,
      };
    } catch (err) {
      return rejectWithValue(toRejection(err, "Erreur lors du partage des documents"));
    }
  }
);

// ─────────────────────────────────────────────
// FETCH DOCUMENT ACCESS LIST
// GET /extraction/sharing/:documentId/access
// ─────────────────────────────────────────────
export const fetchDocumentAccessList = createAsyncThunk(
  "sharing/fetchDocumentAccessList",
  async ({ documentId }, { rejectWithValue }) => {
    try {
      if (!documentId) throw new Error("documentId est requis");

      const response = await axiosExtraction.get(
        EXTRACTION_PATHS.SHARING_ACCESS(documentId)
      );

      // ✅ Gérer les réponses vides
      const data = response?.data || {};

      return {
        success: true,
        documentId,
        accessList: data?.data || data || [],
      };
    } catch (err) {
      return rejectWithValue(toRejection(err, "Erreur chargement de la liste d'accès"));
    }
  }
);

// ─────────────────────────────────────────────
// REVOKE DOCUMENT ACCESS
// DELETE /extraction/sharing/:documentId/access
// Body: { doctorIds: string[] }
// ─────────────────────────────────────────────
export const revokeDocumentAccess = createAsyncThunk(
  "sharing/revokeDocumentAccess",
  async ({ documentId, doctorIds = [] }, { rejectWithValue }) => {
    try {
      if (!documentId) throw new Error("documentId est requis");
      if (!doctorIds.length) throw new Error("Au moins un doctorId est requis");

      const normalizedIds = doctorIds
        .map((id) => String(id ?? "").trim())
        .filter(Boolean);

      const response = await axiosExtraction.delete(
        EXTRACTION_PATHS.SHARING_ACCESS(documentId),
        { data: { doctorIds: normalizedIds } }
      );

      // ✅ Gérer les réponses vides (204 No Content)
      const data = response?.data || {};

      return {
        success: true,
        documentId,
        revokedDoctorIds: normalizedIds,
        message: data?.message || "Accès révoqué avec succès",
      };
    } catch (err) {
      const payload = err?.response?.data;
      if (isAlreadyRevokedOrMissingAccess(payload)) {
        return {
          success: true,
          documentId,
          revokedDoctorIds: normalizedIds,
          revokedCount: 0,
          noop: true,
          message: payload?.message || "Accès déjà révoqué",
        };
      }
      return rejectWithValue(toRejection(err, "Erreur lors de la révocation de l'accès"));
    }
  }
);

// ─────────────────────────────────────────────
// REVOKE ALL ACCESS FOR A DOCTOR (patient side)
// DELETE /extraction/sharing/patient/revoke-all/:doctorId
// ─────────────────────────────────────────────
export const revokeAllAccessForDoctor = createAsyncThunk(
  "sharing/revokeAllAccessForDoctor",
  async ({ patientId, doctorId }, { rejectWithValue }) => {
    try {
      if (!doctorId) throw new Error("doctorId est requis");

      const response = await axiosExtraction.delete(
        EXTRACTION_PATHS.SHARING_PATIENT_REVOKE_ALL(doctorId)
      );

      // ✅ Gérer les réponses vides
      const data = response?.data || {};

      return {
        success: true,
        patientId,
        doctorId,
        revokedCount: data?.revokedCount || 0,
        message: data?.message || "Tous les accès révoqués",
      };
    } catch (err) {
      const payload = err?.response?.data;
      if (isAlreadyRevokedOrMissingAccess(payload)) {
        return {
          success: true,
          patientId,
          doctorId,
          revokedCount: 0,
          noop: true,
          message: payload?.message || "Accès déjà révoqué",
        };
      }
      return rejectWithValue(toRejection(err, "Erreur révocation tous accès médecin"));
    }
  }
);

// ─────────────────────────────────────────────
// EXTEND DOCUMENT ACCESS
// PUT /extraction/sharing/:documentId/access/:doctorId/extend
// Body: { expiresInHours?: number | null }
// ─────────────────────────────────────────────
export const extendDocumentAccess = createAsyncThunk(
  "sharing/extendDocumentAccess",
  async ({ documentId, doctorId, expiresInHours = null }, { rejectWithValue }) => {
    try {
      if (!documentId) throw new Error("documentId est requis");
      if (!doctorId) throw new Error("doctorId est requis");

      const payload = { expiresInHours };

      const response = await axiosExtraction.put(
        EXTRACTION_PATHS.SHARING_EXTEND_ACCESS(documentId, doctorId),
        payload
      );

      // ✅ Gérer les réponses vides
      const data = response?.data || {};

      return {
        success: true,
        documentId,
        doctorId,
        expiresAt: data?.expiresAt || null,
        expiresInHours: data?.expiresInHours || null,
        expiresLabel: data?.expiresLabel || "permanent",
        message: data?.message || "Accès mis à jour",
      };
    } catch (err) {
      return rejectWithValue(toRejection(err, "Erreur extension accès"));
    }
  }
);

// ─────────────────────────────────────────────
// GET DOCTOR SHARED DOCUMENTS
// GET /extraction/sharing/doctor/my-shared-documents
// ─────────────────────────────────────────────
export const getDoctorSharedDocuments = createAsyncThunk(
  "sharing/getDoctorSharedDocuments",
  async ({ skip = 0, limit = 50 } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosExtraction.get(
        `/sharing/doctor/my-shared-documents`,
        { params: { skip, limit } }
      );

      // ✅ Gérer les réponses vides
      const data = response?.data || {};

      return {
        success: true,
        documents: data?.documents || data?.data?.documents || [],
        total: data?.total || data?.data?.total || 0,
      };
    } catch (err) {
      return rejectWithValue(toRejection(err, "Erreur chargement documents partagés médecin"));
    }
  }
);

// ─────────────────────────────────────────────
// GET PATIENT DOCTORS WITH ACCESS
// GET /extraction/sharing/patient/doctors-with-access
// ─────────────────────────────────────────────
export const getPatientDoctorsWithAccess = createAsyncThunk(
  "sharing/getPatientDoctorsWithAccess",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosExtraction.get(
        `/sharing/patient/doctors-with-access`
      );

      // ✅ Gérer les réponses vides
      const data = response?.data || {};

      return {
        success: true,
        doctors: data?.doctors || data?.data?.doctors || [],
        total: data?.totalDoctors || 0,
      };
    } catch (err) {
      return rejectWithValue(toRejection(err, "Erreur chargement médecins avec accès"));
    }
  }
);

// ─────────────────────────────────────────────
// GET SHARING STATISTICS
// GET /extraction/sharing/statistics
// ─────────────────────────────────────────────
export const getSharingStatistics = createAsyncThunk(
  "sharing/getSharingStatistics",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosExtraction.get(`/sharing/statistics`);

      // ✅ Gérer les réponses vides
      const data = response?.data || {};

      return {
        success: true,
        statistics: data?.statistics || data?.data || null,
      };
    } catch (err) {
      return rejectWithValue(toRejection(err, "Erreur chargement statistiques partage"));
    }
  }
);
